import api, { ApiResponse } from '.'
import { MissionsFilters } from '../store/filters'
import { JobTitle } from './job-titles'
import { Employer, InterimAgency } from './organizations'
import { WorkPeriod, WorkPeriodCreationPayload, WorkPeriodWithSummary } from './work-periods'
import { getWeek, getWeekYear } from '../utils/date'
import { User } from './users'
import {
  UserSummaries as _UserSummaries,
  UserWeekSummary as _UserWeekSummary,
  WeekSummary as _WeekSummary,
  WeeksSummaryStatus as _WeeksSummaryStatus,
} from '../../../backend/src/services/resources/missions/missions.summary.service.types'
import { Service } from './services'
import { MissionSchema } from '../../../backend/src/services/resources/missions/mission.model'
import { HistoryItem } from '../components/history/history'
import { MutationFunction } from '@tanstack/react-query'
import { SummaryData } from '../../../backend/src/services/resources/period-summaries/period-summary.types'
import { WorkPeriodTag } from './work-period-tags'
import { addMilliseconds, subMilliseconds } from 'date-fns'

/**
 * Single day slot within a mission week pattern.
 * Backend: 0 = Monday, 6 = Sunday.
 */
export interface MissionScheduleDaySlot {
  startHour?: number
  endHour?: number
}

/**
 * Week pattern: keys 0–6 (Monday–Sunday), absent = no work that day.
 */
export interface MissionScheduleWeekPattern {
  days: Partial<Record<0 | 1 | 2 | 3 | 4 | 5 | 6, MissionScheduleDaySlot>>
}

/**
 * Schedule template for template-based missions.
 * Validity is [start, end] (end null = no end date). Week pattern cycle is anchored by start.
 */
export interface MissionScheduleTemplate {
  /** Start date of the template validity. */
  start: Date
  /** End date of the template validity (null = still active / no end). */
  end: Date | null
  weekPatterns: MissionScheduleWeekPattern[]
}

export type FetchMissionsParams = MissionsFilters

export interface ExportSummaryParams extends FetchMissionsParams {
  type: 'csv' | 'xls'
}

export interface SummaryFilters extends MissionsFilters {
  week?: number
  year?: number
}

export interface WeeksSummaryParams extends Omit<Partial<MissionsFilters>, 'start' | 'end'> {
  from: Date
  to: Date
}

type FetchSummaryParams = {
  year: number
  week: number
  [key: string]: any
}

//TODO: use backend types
export type MissionSummary = {
  weeks: {
    [key: string]: SummaryData
  }
}

export interface MissionWithSummary extends Mission {
  summary: MissionSummary
  workPeriods: WorkPeriodWithSummary[]
  __actions: Mission['__actions'] & { weekCanBeValidated?: boolean }
  weekIsValidated?: boolean
}

export type UserWeekSummary = Omit<_UserWeekSummary, 'user'> & {
  user: User
  missions: MissionWithSummary[]
}

export interface MissionWithSummaries extends MissionWithSummary {
  // TEMP: used by summary table
  //  to be deleted when the summary table will be refactored and use directly query data
  weekSummary: UserWeekSummary
}

export interface Mission
  extends Omit<
    MissionSchema,
    | 'interimAgency'
    | 'workPeriods'
    | '_id'
    | 'jobTitle'
    | 'service'
    | 'user'
    | 'employer'
    | 'history'
    | 'tags'
  > {
  //TODO Import from backend!
  _id: string
  workPeriods: WorkPeriod[]
  service: Service
  user: User
  interimAgency?: InterimAgency
  jobTitle: JobTitle
  employer: Employer
  history: HistoryItem[]
  tags?: WorkPeriodTag[]
}

export type UserSummaries = _UserSummaries

export type WeeksSummary = UserSummaries[]

export type WeekSummary = _WeekSummary

export type WeeksSummaryStatus = _WeeksSummaryStatus

const POPULATE = [
  'workPeriods.events.item',
  'workPeriods.start.clocking',
  'workPeriods.start.comments.user',
  'workPeriods.end.comments.user',
  'workPeriods.end.clocking',
  'workPeriods.service',
  'workPeriods.tags',
  'user',
  'employer',
  'interimAgency',
  'jobTitle',
  'service',
  'tags',
]

/**
 * Builds fetch params for missions list/count.
 * When both start and end are set (week range), returns missions that overlap the week
 * or have no end date: start <= end of week AND (end >= start of week OR end === null).
 */
const buildFetchParams = (params: Partial<FetchMissionsParams> | Partial<SummaryFilters>) => {
  const { start, end, 'workPeriods.periodSummary': periodSummary, ...rest } = params
  const res: any = {
    sort: 'user.lastName',
    summary: true,
    populate: POPULATE,
    ...rest,
  }

  if (periodSummary?.length === 1 && (periodSummary[0] === 'true' || periodSummary[0] === true))
    res['workPeriods.periodSummary'] = ''
  // Quickfix: refactor this when we refactor the filters
  else if (
    periodSummary?.length === 1 &&
    (periodSummary[0] === 'false' || periodSummary[0] === false)
  )
    res['!workPeriods.periodSummary'] = ''
  // Quickfix: refactor this when we refactor the filters
  else if (periodSummary?.length === 1) res['workPeriods.periodSummary'] = periodSummary[0]

  // Week range [start, end]: missions that overlap the week or have no end (end === null).
  // Overlap = mission starts before or during the week AND (mission ends after or during the week OR end is null).
  if (start != null && end != null) {
    res.filter = JSON.stringify({
      start: { $lte: end },
      $or: [{ end: { $gte: start } }, { end: null }],
    })
  } else {
    res['end>'] = start ? subMilliseconds(start, 1) : undefined
    res['start<'] = end ? addMilliseconds(end, 1) : undefined
  }

  return res
}

const buildFetchSummaryParams = (params: Partial<SummaryFilters>): FetchSummaryParams => {
  const res = buildFetchParams(params)
  delete res['start>']
  delete res['start<']
  res.week = params.week || getWeek(new Date(params.start as Date))
  res.year = params.year || getWeekYear(new Date(params.start as Date))
  return res
}

const buildFetchSummariesParams = (params: Partial<WeeksSummaryParams>): FetchSummaryParams => {
  const res = buildFetchParams(params)
  delete res.week
  delete res.year
  return res
}

export const fetch = async (
  params: Partial<FetchMissionsParams>,
): Promise<ApiResponse<Mission[]>> =>
  await api({
    endpoint: '/missions',
    method: 'get',
    params: buildFetchParams(params),
  })

export const count = async (params: FetchMissionsParams): Promise<ApiResponse<number>> =>
  await api({
    endpoint: '/missions/count',
    method: 'get',
    params: buildFetchParams(params),
  })

export const fetchSummary = async (
  params: Partial<MissionsFilters>,
): Promise<ApiResponse<UserWeekSummary[]>> =>
  await api({
    endpoint: '/missions/summary',
    method: 'get',
    params: buildFetchSummaryParams(params),
  })

export const fetchWeeksSummary = async (
  params: WeeksSummaryParams,
): Promise<ApiResponse<WeeksSummary>> =>
  await api({
    endpoint: '/missions/weeks-summary',
    method: 'get',
    params: buildFetchSummariesParams(params),
  })

export const fetchWeeksSummaryStatus = async (
  params: WeeksSummaryParams,
): Promise<ApiResponse<WeeksSummaryStatus>> =>
  await api({
    endpoint: '/missions/weeks-summary/status',
    method: 'get',
    params: buildFetchSummariesParams(params),
  })

export const get = async (id: string): Promise<ApiResponse<Mission>> =>
  await api({
    endpoint: `/missions/${id}`,
    method: 'get',
    params: {
      populate: POPULATE,
      summary: true,
    },
  })

export const exportSummary = async (params: ExportSummaryParams): Promise<ApiResponse<string>> => {
  const { start, end, type, ...rest } = params
  const res = await api({
    endpoint: '/missions/summary/export',
    method: 'get',
    params: {
      sort: 'user.lastName',
      ...buildFetchParams(rest),
      from: start,
      to: end,
      type,
    },
  })

  return res as ApiResponse<string>
}

export type MissionCreationPayload = {
  user: User['_id']
  jobTitle: JobTitle['_id']
  service: Mission['_id']
  address: Mission['address']
  tags?: string[]
  /** Mode "dates fixes": list of fixed slots. Omit when using scheduleTemplate. */
  workPeriods?: Array<Omit<WorkPeriodCreationPayload, 'missionId'>>
  /** Mode "planning récurrent": template-based schedule. Omit when using workPeriods. */
  scheduleTemplate?: MissionScheduleTemplate
}

export const create = async (params: MissionCreationPayload): Promise<ApiResponse<Mission>> => {
  if (params.workPeriods) {
    params.workPeriods = params.workPeriods.filter(w => w)
  }
  return await api({
    endpoint: '/missions',
    method: 'post',
    params,
  })
}

export type MissionUpdatePayload = Partial<
  Pick<MissionCreationPayload, 'service' | 'workPeriods' | 'address'>
> &
  Pick<Mission, '_id'>

export const update = async (params: MissionUpdatePayload): Promise<ApiResponse<Mission>> => {
  const { _id, ...payload } = params
  return await api({
    endpoint: `/missions/${_id}`,
    method: 'put',
    params: payload,
  })
}

export type UnAbandonMissionPayload = {
  missionId: Mission['_id']
}

export const unAbandon: MutationFunction<
  ApiResponse<Mission>,
  UnAbandonMissionPayload
> = async data => {
  const { missionId } = data
  return await api({
    endpoint: `/missions/${missionId}/unabandon`,
    method: 'put',
  })
}
